package com.siriusxm.pia.rest.unifiedaggregator

import androidx.compose.runtime.Composable
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text


@Composable
fun CloudEventDiffView(change: CloudEventChange) {
    val diffs = diffJson(change.key, change.oldValue, change.value)

    Div({
        style {
            marginBottom(12.px)
        }
    }) {
        Div({
            style {
                fontWeight("bold")
                fontSize(1.cssRem)
                marginBottom(4.px)
            }
        }) {
            Text(change.key)
        }

        if (diffs.isEmpty()) {
            Div({
                style {
                    fontStyle("italic")
                    color(Color.gray)
                    padding(8.px)
                    fontSize(0.85.cssRem)
                }
            }) {
                Text("No changes in this update.")
            }
        } else {
            for (diff in diffs) {
                DiffRowSideBySide(diff.path, diff.oldValue, diff.newValue)
            }
        }
    }
}

@Composable
fun DiffRowSideBySide(label: String, oldValue: JsonElement?, newValue: JsonElement?) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(16.px)
            marginBottom(6.px)
            fontSize(0.85.cssRem)
        }
    }) {
        fun renderedValue(value: JsonElement?): String {
            return if (value is JsonPrimitive) {
                value.jsonPrimitive.content
            } else {
                value?.toString() ?: "<empty>"
            }
        }


        // Old value
        Div({
            style {
                padding(8.px)
                flex(1)
                borderRadius(4.px)
            }
        }) {
            Text(renderedValue(oldValue))
        }

        // New value
        Div({
            style {
                padding(8.px)
                flex(1)
                borderRadius(4.px)
            }
        }) {
            Text(renderedValue(newValue))
        }
    }
}

@Composable
fun CloudEventChangesView(changes: CloudEventChanges) {
    for (change in changes.changes) {
        CloudEventDiffView(change)
    }
}

data class FieldDiff(
    val path: String,
    val oldValue: JsonElement?,
    val newValue: JsonElement?
)

fun diffJson(path: String, oldElem: JsonElement?, newElem: JsonElement?): List<FieldDiff> {
    if (oldElem == newElem) return emptyList()

    return when {
        oldElem is JsonObject && newElem is JsonObject -> {
            val keys = oldElem.keys union newElem.keys
            keys.flatMap { key ->
                val newPath = if (path.isEmpty()) key else "$path.$key"
                diffJson(newPath, oldElem[key], newElem[key])
            }
        }
        oldElem is JsonArray && newElem is JsonArray -> {
            val allObjectsWithIds = oldElem.all { it is JsonObject && it.jsonObject.containsKey("id") } &&
                    newElem.all { it is JsonObject && it.jsonObject.containsKey("id") }
            if (allObjectsWithIds) {
                // Treat array as unordered set of identifiable objects
                val oldMap = oldElem.associateBy { it.jsonObject["id"]?.jsonPrimitive?.content.orEmpty() }
                val newMap = newElem.associateBy { it.jsonObject["id"]?.jsonPrimitive?.content.orEmpty() }
                val allIds = oldMap.keys union newMap.keys

                allIds.flatMap { id ->
                    val oldItem = oldMap[id]
                    val newItem = newMap[id]
                    diffJson("$path[id=$id]", oldItem, newItem)
                }
            } else {
                // Fallback to index-based diff
                val maxSize = maxOf(oldElem.size, newElem.size)
                (0 until maxSize).flatMap { index ->
                    val oldItem = oldElem.getOrNull(index)
                    val newItem = newElem.getOrNull(index)
                    diffJson("$path[$index]", oldItem, newItem)
                }
            }
        }
        else -> listOf(FieldDiff(path, oldElem, newElem))
    }
}
